import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, Clock, List } from 'lucide-react';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { formatGroupedNumber } from 'lib/dateFormat';
import { formatRange, subjectCardRanges } from 'modules/test/public-subject';
import type { PublicSubjectCard } from 'modules/test/types';
import { categorySurface, categoryTheme } from './categoryThemes';
import type { SubjectLevel } from './subjectLevel';

interface SubjectCardProps {
  card: PublicSubjectCard;
  /** The subject's name in the learner's language. */
  label: string;
  /** The category's label; the raw slug is never shown. */
  categoryLabel?: string;
  /** How many of this subject's variants the learner has begun. */
  inProgressCount: number;
  onOpen: () => void;
  /** Gives the card its category's accent: a tinted surface, a watermark icon and a coloured initial. */
  themed?: boolean;
  /** The learner's grade and best score in this subject; the card shows both when they have begun it. */
  level?: SubjectLevel | null;
}

/**
 * One subject of the catalog: its name, how many variants it holds, how long and how big they are, and what
 * they cost. Begun variants get a band of their own, so the action never has to share a row with them.
 * The action's hit area covers the card, so the whole card opens the variants.
 */
export function SubjectCard({ card, label, categoryLabel, inProgressCount, onOpen, themed = false, level = null }: SubjectCardProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const { minutes, questions } = subjectCardRanges(card);
  const hasCost = card.freeCount > 0 || card.planCount > 0 || card.minPaidPriceSom !== null;
  const { accent, icon: Watermark } = categoryTheme(card.category);
  const { background, borderColor } = categorySurface(accent);

  return (
    <article
      style={themed ? { background, borderColor } : undefined}
      className="group relative flex min-h-[248px] flex-col overflow-hidden rounded-xl border border-border bg-card transition-[transform,border-color,box-shadow] duration-200 hover:border-foreground/25 hover:shadow-md motion-safe:hover:-translate-y-0.5">
      {themed && (
        <Watermark
          aria-hidden="true"
          strokeWidth={1.2}
          className="pointer-events-none absolute -right-5 -top-5 h-28 w-28 -rotate-6 opacity-[0.12]"
          style={{ color: accent }}
        />
      )}
      {inProgressCount > 0 && (
        <div className="flex items-center gap-2 border-b border-primary-600/30 bg-primary/25 px-5 py-2 text-[13px] font-semibold text-foreground">
          <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-foreground motion-safe:animate-pulse" />
          <span className="min-w-0 truncate">{t('in_progress')}</span>
          <span className="ml-auto tabular-nums">{inProgressCount}</span>
        </div>
      )}
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start gap-3 max-md:flex-wrap">
          <span
            aria-hidden="true"
            style={themed ? { background: accent } : undefined}
            className={cn(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl font-display text-xl font-extrabold',
              themed ? 'text-white' : 'bg-primary text-foreground ring-1 ring-primary-600/60',
            )}
          >
            {label.trim().charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0 flex-1 max-md:basis-36">
            {categoryLabel && <p className="text-[12.5px] font-medium text-muted-foreground [overflow-wrap:anywhere]">{categoryLabel}</p>}
            <h3
              id={titleId}
              className="line-clamp-3 break-words font-display text-[18px] md:line-clamp-2 font-bold leading-[1.25] tracking-[-0.01em] text-foreground"
            >
              {label}
            </h3>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5 max-md:flex-row max-md:items-center">
            <span className="rounded-full bg-muted px-2.5 py-1 text-[12px] font-semibold tabular-nums text-foreground/80">
              {t('portal_tests_subject_variants', { count: card.variantsCount })}
            </span>
            {level?.level && (
              <span
                style={{ background: accent }}
                className="rounded-full px-2.5 py-0.5 font-display text-[13px] font-extrabold text-white"
                aria-label={t('portal_subject_level_badge', { level: level.level })}
              >
                {level.level}
              </span>
            )}
          </div>
        </div>

        {(minutes || questions) && (
          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-[13.5px] text-foreground/80">
            {minutes && (
              <span className="inline-flex items-center gap-1.5">
                <Clock size={15} className="shrink-0 text-muted-foreground/70" aria-hidden="true" />
                <span className="tabular-nums">{formatRange(minutes)}</span> {t('minutes')}
              </span>
            )}
            {questions && (
              <span className="inline-flex items-center gap-1.5">
                <List size={15} className="shrink-0 text-muted-foreground/70" aria-hidden="true" />
                <span className="tabular-nums">{formatRange(questions)}</span> {t('pager_noun_questions')}
              </span>
            )}
          </div>
        )}

        {level && level.attempts > 0 && (
          <p className="mt-3 text-[13px] font-medium tabular-nums text-foreground/80">
            {level.ball !== null ? t('portal_subject_attempts', { count: level.attempts, ball: level.ball }) : t('portal_subject_attempts_only', { count: level.attempts })}
          </p>
        )}

        {hasCost && (
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-muted-foreground">
            {card.freeCount > 0 && <span className="font-semibold text-success-700 dark:text-success-400">{t('free')}</span>}
            {card.planCount > 0 && (
              <span className="inline-flex items-center gap-1">
                <Check size={14} strokeWidth={2.5} className="shrink-0 text-success-600" aria-hidden="true" />
                {t('portal.plan_included')}
              </span>
            )}
            {card.minPaidPriceSom !== null && (
              <span className="tabular-nums">
                {t('portal_tests_price_from', { price: `${formatGroupedNumber(card.minPaidPriceSom)} ${t('portal_tests_currency')}` })}
              </span>
            )}
          </div>
        )}

        {/* Keeps the action off the cost row when the card is full, and pushes it to the foot when it is not. */}
        <div aria-hidden="true" className="mt-auto min-h-4" />
        <Button
          variant="ink"
          size="sm"
          onClick={onOpen}
          aria-describedby={titleId}
          className="h-11 w-full justify-between gap-2 px-3.5 text-[13.5px] lg:h-9 after:absolute after:inset-0 after:content-[''] [&>svg]:transition-transform motion-safe:group-hover:[&>svg]:translate-x-0.5"
        >
          {t('portal_tests_subject_open')}
          <ArrowRight size={15} aria-hidden="true" />
        </Button>
      </div>
    </article>
  );
}

export default SubjectCard;
